/**
 * Profile — the signed-in user's self-service profile editor (web port).
 *
 * D-021 Path A. Web counterpart of apps/mobile/app/profile-edit.tsx. Loads the
 * caller's own profile via GET /me/profile and lets them change their display
 * name + preferred language, persisted via PATCH /me/profile on an explicit
 * Save. Phone is rendered READ-ONLY: changing a verified phone touches
 * identity/KYC and is intentionally out of scope here.
 *
 * Web has no i18n framework yet (TD-072), so there is no live `setLanguage` to
 * call like mobile does. Instead, on a successful Save we mirror the mobile
 * intent by writing the chosen language to localStorage under `fse_lang` — the
 * same key the Settings "Language" picker reads/writes — so the two surfaces
 * stay consistent and a future i18next bootstrap can pick it up.
 *
 * Contract verified against services/api/src/me/dto.ts via the manual client
 * src/api/manual/me.ts (validate-API-before-smoke rule).
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  LanguagePicker,
  Stack,
  Text,
  TextField,
} from '@fse/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { meControllerGetProfile, meControllerUpdateProfile } from '../api/generated/me/me';
import type {
  MyProfileDto,
  MyProfileDtoPreferredLang,
  UpdateMyProfileDto,
} from '../api/generated/models';

// Mirror the SUPPORTED_LANGUAGES / labels in _app.settings.tsx — keep in sync.
const SUPPORTED_LANGUAGES: readonly MyProfileDtoPreferredLang[] = ['en', 'xi', 'nso', 'ts'];

const LANGUAGE_LABELS: Record<MyProfileDtoPreferredLang, string> = {
  en: 'English',
  xi: 'isiXhosa',
  nso: 'Sepedi',
  ts: 'Xitsonga',
};

// LanguagePicker shows the native script first + an English subtitle. We keep
// the same labels as before — `native` is the script name, `english` is the
// language code shown beneath so the original "(code)" hint is preserved.
const LANGUAGE_OPTIONS = SUPPORTED_LANGUAGES.map((code) => ({
  code,
  native: LANGUAGE_LABELS[code],
  english: code,
}));

// localStorage key the Settings language picker reads/writes. We keep it in
// step on a successful Save so both surfaces agree (see file header).
const LANG_KEY = 'fse_lang';

export function ProfileScreen() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const profileQuery = useQuery({
    queryKey: ['me-profile'],
    queryFn: ({ signal }) => meControllerGetProfile(signal),
  });

  // Editable fields — seeded from the loaded profile once, then owned by the
  // form. `seeded` guards against re-clobbering edits on a background refetch.
  const [displayName, setDisplayName] = useState('');
  const [selectedLang, setSelectedLang] = useState<MyProfileDtoPreferredLang>('en');
  const [seeded, setSeeded] = useState(false);
  const [showNameError, setShowNameError] = useState(false);
  const [saved, setSaved] = useState(false);

  const profile = profileQuery.data;

  useEffect(() => {
    if (!seeded && profile) {
      setDisplayName(profile.displayName);
      setSelectedLang(profile.preferredLang);
      setSeeded(true);
    }
  }, [seeded, profile]);

  const saveMutation = useMutation({
    mutationFn: (patch: UpdateMyProfileDto) => meControllerUpdateProfile(patch),
    onSuccess: (updated: MyProfileDto) => {
      // Prime the cache so the form + any other reader show the saved values.
      queryClient.setQueryData(['me-profile'], updated);
      // Keep the web language preference in step with the saved value so the
      // Settings "Language" picker reflects it (web has no live i18n switch).
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(LANG_KEY, updated.preferredLang);
      }
      setSaved(true);
    },
  });

  function onSave() {
    setSaved(false);
    const trimmedName = displayName.trim();
    if (trimmedName.length === 0) {
      setShowNameError(true);
      return;
    }
    setShowNameError(false);

    // Only send fields that actually changed from the loaded profile.
    const patch: UpdateMyProfileDto = {};
    if (profile && trimmedName !== profile.displayName) {
      patch.displayName = trimmedName;
    }
    if (profile && selectedLang !== profile.preferredLang) {
      patch.preferredLang = selectedLang;
    }

    // Nothing changed server-side — treat Save as a no-op confirmation rather
    // than provoking a 400 from the empty-patch refine. Still reconcile the
    // local language preference to the selection (pressing Save asserts it).
    if (patch.displayName === undefined && patch.preferredLang === undefined) {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(LANG_KEY, selectedLang);
      }
      setSaved(true);
      return;
    }

    saveMutation.mutate(patch);
  }

  const isSaving = saveMutation.isPending;
  const nameInvalid = showNameError && displayName.trim().length === 0;

  // LanguagePicker reports the chosen `code` directly (no DOM event). Selection
  // is staged and only persisted on an explicit Save (unchanged behaviour).
  function onLangChange(code: string) {
    setSelectedLang(code as MyProfileDtoPreferredLang);
    setSaved(false);
  }

  return (
    <Stack spacing={3} sx={{ maxWidth: 640 }}>
      <Box>
        <Text variant="headline-medium" as="h1">
          {t('profile.title')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {t('profile.subtitle')}
          </Text>
        </Box>
      </Box>

      {profileQuery.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : profileQuery.isError || !profile ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => profileQuery.refetch()}>
              {t('profile.retry')}
            </Button>
          }
        >
          {t('profile.loadError', { error: String(profileQuery.error) })}
        </Alert>
      ) : (
        <Card>
          <CardContent>
            <Box sx={{ mb: 2 }}>
              <Text variant="title-medium" as="h2">
                {t('profile.detailsTitle')}
              </Text>
            </Box>
            <Stack spacing={3}>
              {/* Phone — READ-ONLY. Disabled field so it reads as a value, not
                  an editable input. */}
              <TextField
                label={t('profile.phoneLabel')}
                value={profile.phone}
                disabled
                fullWidth
                helperText={t('profile.phoneHelper')}
              />

              {/* Display name — editable */}
              <TextField
                label={t('profile.displayNameLabel')}
                value={displayName}
                onChange={(e) => {
                  setDisplayName(e.target.value);
                  if (showNameError && e.target.value.trim().length > 0) {
                    setShowNameError(false);
                  }
                }}
                slotProps={{ htmlInput: { maxLength: 200 } }}
                error={nameInvalid}
                helperText={
                  nameInvalid ? t('profile.displayNameRequired') : t('profile.displayNameHelper')
                }
                disabled={isSaving}
                fullWidth
              />

              {/* Preferred language — staged, persisted on Save */}
              <Box>
                <Box sx={{ mb: 1 }}>
                  <Text variant="label-large" color="text.primary">
                    {t('profile.preferredLanguage')}
                  </Text>
                </Box>
                <LanguagePicker
                  options={LANGUAGE_OPTIONS}
                  value={selectedLang}
                  onChange={onLangChange}
                  aria-label={t('profile.preferredLanguage')}
                />
              </Box>

              {saveMutation.isError ? (
                <Alert severity="error">
                  {t('profile.saveError', { error: String(saveMutation.error) })}
                </Alert>
              ) : null}

              {saved ? <Alert severity="success">{t('profile.saved')}</Alert> : null}

              <Box>
                <Button variant="contained" onClick={onSave} disabled={isSaving}>
                  {isSaving ? t('profile.saving') : t('profile.save')}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}
